import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Card, Button, Tag, Row, Col, Spin, Empty, message, Avatar, Rate, Modal, Typography, Input, Space, Tooltip, Divider } from "antd";
import { 
  TeamOutlined, UserOutlined, CheckCircleOutlined,
  PlusOutlined, RocketOutlined, FileTextOutlined, LoadingOutlined,
  ReloadOutlined, EditOutlined, SaveOutlined, CopyOutlined, EyeOutlined
} from "@ant-design/icons";
import styled from "styled-components";
import axios from "axios";
import { API_CONST } from "../../../const";

const { Paragraph, Title, Text } = Typography;
const { TextArea } = Input;

const RecommendationsWrapper = styled.div`
  margin-top: 24px;

  .section-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 16px;

    h3 {
      margin: 0;
      color: #014c75;
      display: flex;
      align-items: center;
      gap: 8px;
    }
  }

  .recommendation-card {
    border-radius: 12px;
    margin-bottom: 16px;
    border: 1px solid #f0f0f0;
    transition: all 0.3s;

    &:hover {
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
    }

    .card-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 12px;

      .role-title {
        font-size: 16px;
        font-weight: 600;
        color: #014c75;
      }

      .score-badge {
        background: #e6fffb;
        color: #13c2c2;
        padding: 4px 12px;
        border-radius: 12px;
        font-size: 12px;
        font-weight: 600;
      }
    }

    .description {
      color: #666;
      margin-bottom: 12px;
    }

    .tech-tags {
      margin-bottom: 12px;
    }

    .card-actions {
      display: flex;
      gap: 8px;
    }
  }

  .talent-card {
    border-radius: 12px;
    text-align: center;
    padding: 16px;
    border: 1px solid #f0f0f0;
    transition: all 0.3s;

    &:hover {
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
    }

    .talent-avatar {
      margin-bottom: 12px;
    }

    .talent-name {
      font-weight: 600;
      color: #014c75;
      margin-bottom: 4px;
    }

    .talent-role {
      color: #666;
      font-size: 13px;
      margin-bottom: 8px;
    }

    .match-score {
      background: #f6ffed;
      color: #52c41a;
      padding: 4px 12px;
      border-radius: 12px;
      font-size: 12px;
      font-weight: 600;
      display: inline-block;
    }
  }

  .empty-state {
    text-align: center;
    padding: 40px;
    color: #999;
  }
`;

const ProjectRecommendations = ({ projectId, projectStatus, project, isAdmin = false }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(false);
  const [creatingJobs, setCreatingJobs] = useState(false);
  const [generatingJD, setGeneratingJD] = useState({});
  const [regeneratingJD, setRegeneratingJD] = useState(false);
  const [savingJob, setSavingJob] = useState(false);
  const [jdModalVisible, setJdModalVisible] = useState(false);
  const [currentJD, setCurrentJD] = useState(null);
  const [editedJD, setEditedJD] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [currentSuggestion, setCurrentSuggestion] = useState(null);
  const [generatedJDs, setGeneratedJDs] = useState({});
  const [recommendations, setRecommendations] = useState({
    jobRoleSuggestions: [],
    talentMatches: []
  });

  useEffect(() => {
    if (projectId) {
      fetchRecommendations();
      fetchSavedJDs();
    }
  }, [projectId]);

  const fetchRecommendations = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("authToken");
      const response = await axios.get(
        `${API_CONST.GET_PROJECT_RECOMMENDATIONS}/${projectId}/recommendations`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setRecommendations({
        jobRoleSuggestions: response.data.jobRoleSuggestions || [],
        talentMatches: response.data.talentMatches || []
      });
    } catch (error) {
      console.error("Error fetching recommendations:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchSavedJDs = async () => {
    try {
      const token = localStorage.getItem("authToken");
      const response = await axios.get(
        `${API_CONST.GET_PROJECT_GENERATED_JDS}/${projectId}/generated-jds`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (response.data.generatedJDs) {
        setGeneratedJDs(response.data.generatedJDs);
      }
    } catch (error) {
      console.error("Error fetching saved JDs:", error);
    }
  };

  const handleCreateDraftJobs = async () => {
    setCreatingJobs(true);
    try {
      const token = localStorage.getItem("authToken");
      const response = await axios.post(
        `${API_CONST.CREATE_DRAFT_JOBS}/${projectId}/create-draft-jobs`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      message.success(`${response.data.draftJobs?.length || 0} draft job requirements created successfully!`);
    } catch (error) {
      console.error("Error creating draft jobs:", error);
      message.error("Failed to create draft jobs");
    } finally {
      setCreatingJobs(false);
    }
  };

  const handleViewOrGenerateJD = async (suggestion) => {
    const key = suggestion.title;
    if (generatedJDs[key]) {
      const jdData = generatedJDs[key];
      setCurrentJD(jdData);
      setEditedJD(jdData);
      setCurrentSuggestion(suggestion);
      setIsEditing(false);
      setJdModalVisible(true);
      return;
    }
    await handleGenerateJD(suggestion, false);
  };

  const handleGenerateJD = async (suggestion, isRegenerate = false) => {
    const key = suggestion.title;
    if (isRegenerate) {
      setRegeneratingJD(true);
    } else {
      setGeneratingJD(prev => ({ ...prev, [key]: true }));
    }
    try {
      const token = localStorage.getItem("authToken");
      const response = await axios.post(
        API_CONST.GENERATE_JD,
        {
          jobName: suggestion.title,
          requiredSkills: suggestion.techMatches?.length > 0 ? suggestion.techMatches : suggestion.technologies,
          experienceRange: suggestion.complexity?.includes('high') ? '7-10 years' : '4-6 years',
          projectContext: project?.projectObjective || project?.description,
          architectureType: project?.architectureType,
          deliveryType: project?.deliveryType,
          projectId: projectId,
          engagementId: project?.engagementId
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const jd = response.data.jobDescription;
      const jdData = {
        role: jd.role || suggestion.title,
        jobResponsibilities: jd.jobResponsibilities || "",
        primarySkills: jd.primarySkills || [],
        secondarySkills: jd.secondarySkills || [],
        monthlyBudgetINR: jd.monthlyBudgetINR || 0,
        experienceRange: jd.experienceRange || "",
        tools: jd.tools || [],
        duration: jd.duration || null,
        durationUnit: jd.durationUnit || "months",
        startDate: jd.startDate || new Date().toISOString().split('T')[0],
        location: jd.location || "Remote",
        roleName: suggestion.title
      };
      setCurrentJD(jdData);
      setEditedJD(jdData);
      setCurrentSuggestion(suggestion);
      setIsEditing(false);
      setJdModalVisible(true);
      setGeneratedJDs(prev => ({ ...prev, [key]: jdData }));
      if (isRegenerate) {
        message.success("Job description regenerated successfully!");
      }
    } catch (error) {
      console.error("Error generating JD:", error);
      message.error(error.response?.data?.error || "Failed to generate job description");
    } finally {
      if (isRegenerate) {
        setRegeneratingJD(false);
      } else {
        setGeneratingJD(prev => ({ ...prev, [key]: false }));
      }
    }
  };

  const handleEditField = (field, value) => {
    setEditedJD(prev => ({ ...prev, [field]: value }));
  };

  const handleEditListItem = (field, index, value) => {
    setEditedJD(prev => {
      const list = [...(prev[field] || [])];
      list[index] = value;
      return { ...prev, [field]: list };
    });
  };

  const handleAddListItem = (field) => {
    setEditedJD(prev => ({
      ...prev,
      [field]: [...(prev[field] || []), ""]
    }));
  };

  const handleRemoveListItem = (field, index) => {
    setEditedJD(prev => ({
      ...prev,
      [field]: (prev[field] || []).filter((_, i) => i !== index)
    }));
  };

  const handleUpdateJob = async () => {
    if (!project?.id || !currentSuggestion) {
      message.error("Missing project or job information");
      return;
    }

    setSavingJob(true);
    try {
      const token = localStorage.getItem("authToken");
      const engagementDuration = project?.engagement?.expectedDuration;
      const engagementDurationUnit = project?.engagement?.durationUnit || 'months';
      let monthsOfEngagement = '';
      if (engagementDuration) {
        monthsOfEngagement = `${engagementDuration} ${engagementDurationUnit}`;
      } else {
        const durationValue = editedJD?.duration || currentJD?.duration;
        const durationUnit = editedJD?.durationUnit || currentJD?.durationUnit || 'months';
        if (durationValue) {
          monthsOfEngagement = `${durationValue} ${durationUnit}`;
        } else {
          monthsOfEngagement = '6-12 months';
        }
      }
      
      const jobData = {
        engagementId: project.engagementId,
        projectId: project.id,
        customerOrganization: project.customerOrganization,
        role: editedJD?.role || currentSuggestion.title,
        jobResponsibilities: editedJD?.jobResponsibilities || "",
        primarySkills: editedJD?.primarySkills?.slice(0, 3) || [],
        secondarySkills: editedJD?.secondarySkills?.slice(0, 5) || [],
        experienceRange: editedJD?.experienceRange || currentJD?.experienceRange || '4-6 years',
        status: 'draft',
        budget: editedJD?.monthlyBudgetINR || currentJD?.monthlyBudgetINR || 0,
        budgetPeriod: 'monthly',
        reportingLocation: editedJD?.location || currentJD?.location || 'Remote',
        worldwide: (editedJD?.location || currentJD?.location || 'Remote').toLowerCase() === 'remote',
        monthsOfEngagement: monthsOfEngagement,
        engagementType: 'Full-time',
        numberOfRequirements: 1,
        numberOfRequirementsPeriod: 'total',
        communication: 'English',
        workingTime: 'Flexible',
        workingHoursPerWeek: 40,
        travelPreference: 'None',
        provideSystem: 'No',
        toolsUsed: editedJD?.tools || currentJD?.tools || [],
        shiftTiming: 'Regular',
        tentativeStartDate: editedJD?.startDate || currentJD?.startDate || new Date().toISOString().split('T')[0],
      };

      await axios.post(
        `${API_CONST.ADD_JOB_REQUIREMENTS}`,
        jobData,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      message.success("Draft job created successfully! You can find it in your Job Requirements.");
      setJdModalVisible(false);
    } catch (error) {
      console.error("Error creating job:", error);
      message.error(error.response?.data?.error || "Failed to create job");
    } finally {
      setSavingJob(false);
    }
  };

  const handleTalentClick = (talent) => {
    const basePath = isAdmin ? "/admin/talent-profiles/details" : "/customer/talent-details";
    navigate(`${basePath}/${talent.id}`, { 
      state: { from: location.pathname }
    });
  };

  const handleCopyToClipboard = () => {
    const jdText = formatJDForCopy(editedJD || currentJD);
    navigator.clipboard.writeText(jdText);
    message.success("Job description copied to clipboard!");
  };

  const closeModal = () => {
    setJdModalVisible(false);
    setCurrentJD(null);
    setEditedJD(null);
    setIsEditing(false);
    setCurrentSuggestion(null);
  };

  if (loading) {
    return (
      <RecommendationsWrapper>
        <div style={{ textAlign: "center", padding: 40 }}>
          <Spin size="large" />
        </div>
      </RecommendationsWrapper>
    );
  }

  const { jobRoleSuggestions, talentMatches } = recommendations;

  return (
    <RecommendationsWrapper>
      <Card>
        <div className="section-header">
          <h3><RocketOutlined /> Recommended Job Roles</h3>
          {projectStatus === "active" && jobRoleSuggestions.length > 0 && (
            <Button 
              type="primary" 
              icon={<PlusOutlined />}
              loading={creatingJobs}
              onClick={handleCreateDraftJobs}
              style={{ background: "#01d9a9", borderColor: "#01d9a9" }}
            >
              Create Draft Jobs
            </Button>
          )}
        </div>

        {jobRoleSuggestions.length > 0 ? (
          <Row gutter={[16, 16]}>
            {jobRoleSuggestions.slice(0, 5).map((suggestion, index) => (
              <Col xs={24} md={8} key={index}>
                <Card className="recommendation-card" size="small">
                  <div className="card-header">
                    <span className="role-title">{suggestion.title}</span>
                    <span className="score-badge">
                      Match: {Math.round((suggestion.score / 50) * 100)}%
                    </span>
                  </div>
                  <p className="description">{suggestion.description}</p>
                  <div className="tech-tags">
                    {(suggestion.techMatches || []).slice(0, 4).map((tech, i) => (
                      <Tag key={i} color="blue">{tech}</Tag>
                    ))}
                  </div>
                  <div className="card-actions">
                    <Button
                      type="primary"
                      size="small"
                      icon={generatingJD[suggestion.title] ? <LoadingOutlined /> : (generatedJDs[suggestion.title] ? <EyeOutlined /> : <FileTextOutlined />)}
                      loading={generatingJD[suggestion.title]}
                      onClick={() => handleViewOrGenerateJD(suggestion)}
                      style={{ background: generatedJDs[suggestion.title] ? "#52c41a" : "#014c75", borderColor: generatedJDs[suggestion.title] ? "#52c41a" : "#014c75" }}
                    >
                      {generatedJDs[suggestion.title] ? "View Generated JD" : "Generate JD"}
                    </Button>
                  </div>
                </Card>
              </Col>
            ))}
          </Row>
        ) : (
          <div className="empty-state">
            <TeamOutlined style={{ fontSize: 32, marginBottom: 12 }} />
            <p>No job role suggestions available. Add more technical details to get recommendations.</p>
          </div>
        )}
      </Card>

      <Card style={{ marginTop: 16 }}>
        <div className="section-header">
          <h3><UserOutlined /> Matching Talents</h3>
        </div>

        {talentMatches.length > 0 ? (
          <Row gutter={[16, 16]}>
            {talentMatches.slice(0, 5).map((talent, index) => (
              <Col xs={24} sm={12} md={8} lg={6} key={index}>
                <Card 
                  className="talent-card" 
                  size="small" 
                  style={{ textAlign: "left", padding: "16px", cursor: "pointer" }}
                  onClick={() => handleTalentClick(talent)}
                  hoverable
                >
                  <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 12 }}>
                    <Avatar 
                      size={48} 
                      icon={<UserOutlined />}
                    />
                    <div>
                      <div className="talent-name">{talent.fullName || "Anonymous"}</div>
                      <div className="talent-role">{talent.currentRole || "Developer"}</div>
                    </div>
                  </div>
                  <div style={{ marginBottom: 8, fontSize: 12, color: "#666" }}>
                    <span>{talent.experienceYears || 0}+ years</span>
                    {talent.location && <span> | {talent.location}</span>}
                  </div>
                  <div style={{ marginBottom: 8 }}>
                    {(talent.matchedSkills || talent.primarySkills || []).slice(0, 3).map((skill, i) => (
                      <Tag key={i} color="green" style={{ marginBottom: 4, fontSize: 11 }}>{skill}</Tag>
                    ))}
                    {((talent.matchedSkills || talent.primarySkills || []).length > 3) && (
                      <Tag style={{ fontSize: 11 }}>+{(talent.matchedSkills || talent.primarySkills || []).length - 3}</Tag>
                    )}
                  </div>
                  <div style={{ textAlign: "center" }}>
                    <span className="match-score">
                      <CheckCircleOutlined /> Match Score: {talent.matchScore}
                    </span>
                  </div>
                </Card>
              </Col>
            ))}
          </Row>
        ) : (
          <div className="empty-state">
            <UserOutlined style={{ fontSize: 32, marginBottom: 12 }} />
            <p>No matching talents found. Recommendations are based on project technologies.</p>
          </div>
        )}
      </Card>

      <Modal
        title={
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", width: "100%" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <FileTextOutlined style={{ color: "#014c75" }} />
              <span>Generated Job Description - {editedJD?.role || editedJD?.roleName || currentJD?.role || currentJD?.roleName}</span>
            </div>
            <Space style={{ marginRight: 24 }}>
              <Tooltip title={isEditing ? "View Mode" : "Edit Mode"}>
                <Button 
                  icon={<EditOutlined />} 
                  type={isEditing ? "primary" : "default"}
                  onClick={() => setIsEditing(!isEditing)}
                  size="small"
                >
                  {isEditing ? "Editing" : "Edit"}
                </Button>
              </Tooltip>
            </Space>
          </div>
        }
        open={jdModalVisible}
        onCancel={closeModal}
        footer={
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <Space>
              <Button 
                icon={<ReloadOutlined spin={regeneratingJD} />}
                loading={regeneratingJD}
                onClick={() => currentSuggestion && handleGenerateJD(currentSuggestion, true)}
                disabled={regeneratingJD}
              >
                Regenerate
              </Button>
            </Space>
            <Space>
              <Button onClick={closeModal}>
                Close
              </Button>
              <Button 
                icon={<CopyOutlined />}
                onClick={handleCopyToClipboard}
              >
                Copy to Clipboard
              </Button>
              <Button 
                type="primary"
                icon={<SaveOutlined />}
                loading={savingJob}
                onClick={handleUpdateJob}
                style={{ background: "#01d9a9", borderColor: "#01d9a9" }}
              >
                Create Draft Job
              </Button>
            </Space>
          </div>
        }
        width={900}
      >
        {(editedJD || currentJD) && (
          <div style={{ maxHeight: "60vh", overflowY: "auto", padding: "0 8px" }}>
            {isEditing ? (
              <>
                <div style={{ marginBottom: 16 }}>
                  <Text strong style={{ color: "#014c75", display: "block", marginBottom: 8 }}>Job Title (Role)</Text>
                  <Input
                    value={editedJD?.role || ""}
                    onChange={(e) => handleEditField("role", e.target.value)}
                    size="large"
                  />
                </div>

                <div style={{ marginBottom: 16 }}>
                  <Text strong style={{ color: "#014c75", display: "block", marginBottom: 8 }}>Job Responsibilities</Text>
                  <TextArea
                    value={editedJD?.jobResponsibilities || ""}
                    onChange={(e) => handleEditField("jobResponsibilities", e.target.value)}
                    autoSize={{ minRows: 10, maxRows: 20 }}
                    placeholder="Contains overview, key responsibilities, requirements, and preferred qualifications"
                  />
                </div>

                <div style={{ marginBottom: 16 }}>
                  <Text strong style={{ color: "#014c75", display: "block", marginBottom: 8 }}>Primary Skills (max 3, comma-separated)</Text>
                  <Input
                    value={editedJD?.primarySkills?.join(", ") || ""}
                    onChange={(e) => handleEditField("primarySkills", e.target.value.split(",").map(s => s.trim()).filter(Boolean).slice(0, 3))}
                    placeholder="e.g., React, Node.js, TypeScript"
                  />
                  <Text type="secondary" style={{ fontSize: 12 }}>Limited to 3 skills</Text>
                </div>

                <div style={{ marginBottom: 16 }}>
                  <Text strong style={{ color: "#014c75", display: "block", marginBottom: 8 }}>Secondary Skills (max 5, comma-separated)</Text>
                  <Input
                    value={editedJD?.secondarySkills?.join(", ") || ""}
                    onChange={(e) => handleEditField("secondarySkills", e.target.value.split(",").map(s => s.trim()).filter(Boolean).slice(0, 5))}
                    placeholder="e.g., MongoDB, Redis, Docker, AWS, GraphQL"
                  />
                  <Text type="secondary" style={{ fontSize: 12 }}>Limited to 5 skills</Text>
                </div>

                <Divider />

                <Row gutter={16}>
                  <Col span={8}>
                    <div style={{ marginBottom: 16 }}>
                      <Text strong style={{ color: "#014c75", display: "block", marginBottom: 8 }}>Monthly Budget (INR)</Text>
                      <Input
                        type="number"
                        value={editedJD?.monthlyBudgetINR || ""}
                        onChange={(e) => handleEditField("monthlyBudgetINR", parseInt(e.target.value) || 0)}
                        prefix="₹"
                        placeholder="e.g., 150000"
                      />
                    </div>
                  </Col>
                  <Col span={8}>
                    <div style={{ marginBottom: 16 }}>
                      <Text strong style={{ color: "#014c75", display: "block", marginBottom: 8 }}>Experience Range</Text>
                      <Input
                        value={editedJD?.experienceRange || ""}
                        onChange={(e) => handleEditField("experienceRange", e.target.value)}
                        placeholder="e.g., 5-7 years"
                      />
                    </div>
                  </Col>
                  <Col span={8}>
                    <div style={{ marginBottom: 16 }}>
                      <Text strong style={{ color: "#014c75", display: "block", marginBottom: 8 }}>Location</Text>
                      <Input
                        value={editedJD?.location || ""}
                        onChange={(e) => handleEditField("location", e.target.value)}
                        placeholder="e.g., Remote, Bangalore"
                      />
                    </div>
                  </Col>
                </Row>

                <Row gutter={16}>
                  <Col span={8}>
                    <div style={{ marginBottom: 16 }}>
                      <Text strong style={{ color: "#014c75", display: "block", marginBottom: 8 }}>Duration</Text>
                      <Input
                        value={editedJD?.duration ? `${editedJD.duration} ${editedJD.durationUnit || 'months'}` : ""}
                        onChange={(e) => {
                          const match = e.target.value.match(/(\d+)\s*(\w+)?/);
                          if (match) {
                            handleEditField("duration", parseInt(match[1]) || null);
                            if (match[2]) handleEditField("durationUnit", match[2]);
                          }
                        }}
                        placeholder="e.g., 6 months"
                      />
                    </div>
                  </Col>
                  <Col span={8}>
                    <div style={{ marginBottom: 16 }}>
                      <Text strong style={{ color: "#014c75", display: "block", marginBottom: 8 }}>Start Date</Text>
                      <Input
                        type="date"
                        value={editedJD?.startDate || ""}
                        onChange={(e) => handleEditField("startDate", e.target.value)}
                      />
                    </div>
                  </Col>
                  <Col span={8}>
                    <div style={{ marginBottom: 16 }}>
                      <Text strong style={{ color: "#014c75", display: "block", marginBottom: 8 }}>Tools Used (comma-separated)</Text>
                      <Input
                        value={editedJD?.tools?.join(", ") || ""}
                        onChange={(e) => handleEditField("tools", e.target.value.split(",").map(s => s.trim()).filter(Boolean).slice(0, 5))}
                        placeholder="e.g., Jira, Docker, AWS"
                      />
                    </div>
                  </Col>
                </Row>
              </>
            ) : (
              <>
                <Title level={4}>{editedJD?.role || currentJD?.role}</Title>
                
                <div style={{ marginBottom: 16 }}>
                  <Title level={5} style={{ color: "#014c75", marginBottom: 8 }}>Job Responsibilities</Title>
                  <Paragraph style={{ whiteSpace: "pre-wrap" }}>{editedJD?.jobResponsibilities || currentJD?.jobResponsibilities}</Paragraph>
                </div>

                <div style={{ marginBottom: 16 }}>
                  <Title level={5} style={{ color: "#014c75", marginBottom: 8 }}>Primary Skills</Title>
                  <div>
                    {(editedJD?.primarySkills || currentJD?.primarySkills)?.slice(0, 3).map((skill, i) => (
                      <Tag key={i} color="blue" style={{ marginBottom: 4 }}>{skill}</Tag>
                    ))}
                  </div>
                </div>

                <div style={{ marginBottom: 16 }}>
                  <Title level={5} style={{ color: "#014c75", marginBottom: 8 }}>Secondary Skills</Title>
                  <div>
                    {(editedJD?.secondarySkills || currentJD?.secondarySkills)?.slice(0, 5).map((skill, i) => (
                      <Tag key={i} color="green" style={{ marginBottom: 4 }}>{skill}</Tag>
                    ))}
                  </div>
                </div>

                <Divider />

                <Row gutter={16}>
                  <Col span={8}>
                    <div style={{ marginBottom: 16 }}>
                      <Title level={5} style={{ color: "#014c75", marginBottom: 8 }}>Monthly Budget</Title>
                      <Text style={{ fontSize: 16 }}>
                        ₹{((editedJD?.monthlyBudgetINR || currentJD?.monthlyBudgetINR) || 0).toLocaleString('en-IN')}
                      </Text>
                    </div>
                  </Col>
                  <Col span={8}>
                    <div style={{ marginBottom: 16 }}>
                      <Title level={5} style={{ color: "#014c75", marginBottom: 8 }}>Experience</Title>
                      <Text style={{ fontSize: 16 }}>{editedJD?.experienceRange || currentJD?.experienceRange || "Not specified"}</Text>
                    </div>
                  </Col>
                  <Col span={8}>
                    <div style={{ marginBottom: 16 }}>
                      <Title level={5} style={{ color: "#014c75", marginBottom: 8 }}>Location</Title>
                      <Text style={{ fontSize: 16 }}>{editedJD?.location || currentJD?.location || "Remote"}</Text>
                    </div>
                  </Col>
                </Row>

                <Row gutter={16}>
                  <Col span={8}>
                    <div style={{ marginBottom: 16 }}>
                      <Title level={5} style={{ color: "#014c75", marginBottom: 8 }}>Duration</Title>
                      <Text style={{ fontSize: 16 }}>
                        {(editedJD?.duration || currentJD?.duration) 
                          ? `${editedJD?.duration || currentJD?.duration} ${editedJD?.durationUnit || currentJD?.durationUnit || 'months'}`
                          : "Not specified"}
                      </Text>
                    </div>
                  </Col>
                  <Col span={8}>
                    <div style={{ marginBottom: 16 }}>
                      <Title level={5} style={{ color: "#014c75", marginBottom: 8 }}>Start Date</Title>
                      <Text style={{ fontSize: 16 }}>{editedJD?.startDate || currentJD?.startDate || "Not specified"}</Text>
                    </div>
                  </Col>
                  <Col span={8}>
                    <div style={{ marginBottom: 16 }}>
                      <Title level={5} style={{ color: "#014c75", marginBottom: 8 }}>Tools Used</Title>
                      <div>
                        {(editedJD?.tools || currentJD?.tools)?.map((tool, i) => (
                          <Tag key={i} color="purple" style={{ marginBottom: 4 }}>{tool}</Tag>
                        ))}
                      </div>
                    </div>
                  </Col>
                </Row>
              </>
            )}
          </div>
        )}
      </Modal>
    </RecommendationsWrapper>
  );
};

const formatJDForCopy = (jd) => {
  if (!jd) return "";
  
  let text = `${jd.role || jd.roleName}\n\n`;
  text += jd.jobResponsibilities || "";
  text += `\n\nPRIMARY SKILLS\n`;
  text += jd.primarySkills?.join(", ") || "";
  text += `\n\nSECONDARY SKILLS\n`;
  text += jd.secondarySkills?.join(", ") || "";
  text += `\n\nJOB DETAILS\n`;
  text += `Monthly Budget: ₹${(jd.monthlyBudgetINR || 0).toLocaleString('en-IN')}\n`;
  text += `Experience: ${jd.experienceRange || "Not specified"}\n`;
  text += `Location: ${jd.location || "Remote"}\n`;
  text += `Duration: ${jd.duration ? `${jd.duration} ${jd.durationUnit || 'months'}` : "Not specified"}\n`;
  text += `Start Date: ${jd.startDate || "Not specified"}\n`;
  text += `Tools: ${jd.tools?.join(", ") || "Not specified"}`;
  
  return text;
};

export default ProjectRecommendations;
